import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import AudioPlayer from "../../components/AudioPlayer/AudioPlayer";
import { Button } from "../../components/ui/button";
import {
  AccessiblePublicationFile,
  PublicationFileFormat,
} from "../../types/Publications";
import s from "./Publications.module.css";

type PublicationMediaViewerProps = {
  material: AccessiblePublicationFile;
  isRefreshing: boolean;
  error: string | null;
  onRefresh: () => void;
};

const PreviewError = ({
  message,
  onRefresh,
}: {
  message: string;
  onRefresh: () => void;
}) => (
  <div className={s.previewState} role="alert">
    <strong>Preview unavailable</strong>
    <span>{message}</span>
    <Button type="button" onClick={onRefresh}>
      Retry preview
    </Button>
  </div>
);

const PublicationMediaViewer = ({
  material,
  isRefreshing,
  error,
  onRefresh,
}: PublicationMediaViewerProps) => {
  if (isRefreshing) {
    return (
      <div className={s.previewState} role="status">
        Refreshing secure preview…
      </div>
    );
  }
  if (error) return <PreviewError message={error} onRefresh={onRefresh} />;
  if (!material.url) {
    return (
      <PreviewError
        message="Secure material access is unavailable."
        onRefresh={onRefresh}
      />
    );
  }

  if (material.format === PublicationFileFormat.AUDIO) {
    return (
      <div
        className={s.audioPreview}
        aria-label={`Audio preview: ${material.title}`}
      >
        <AudioPlayer
          key={material.url}
          url={material.url}
          downloadIsAvailable={false}
          onError={onRefresh}
        />
      </div>
    );
  }

  return (
    <div className={s.pdfPreview} aria-label={`PDF preview: ${material.title}`}>
      <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.js">
        <div style={{ height: "60svh" }}>
          <Viewer
            key={material.url}
            fileUrl={material.url}
            renderError={() => (
              <PreviewError
                message="The PDF could not be opened with its current secure URL."
                onRefresh={onRefresh}
              />
            )}
          />
        </div>
      </Worker>
    </div>
  );
};

export default PublicationMediaViewer;
